import React, { useState, useCallback, useRef } from 'react';
import { Container, Row, Col, Card, Button, Form, ProgressBar, Alert } from 'react-bootstrap';
import imageCompression from 'browser-image-compression';
import 'line-awesome/dist/line-awesome/css/line-awesome.min.css';
import '../../styles/ImageReducer.css';

const ImageReducer = () => {
  const [originalImages, setOriginalImages] = useState([]);
  const [compressedImages, setCompressedImages] = useState([]);
  const [quality, setQuality] = useState(75);
  const [isCompressing, setIsCompressing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [dragActive, setDragActive] = useState(false);
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFiles(e.dataTransfer.files);
    }
  }, []);

  const handleChange = useCallback((e) => {
    e.preventDefault();
    if (e.target.files && e.target.files.length > 0) {
      handleFiles(e.target.files);
    }
  }, []);

  const handleFiles = (files) => {
    if (originalImages.length + files.length > 10) {
      setError('Maximum 10 images allowed. Please remove some images first.');
      return;
    }
    
    setError('');
    const validFiles = Array.from(files).filter(file => 
      file.type === 'image/jpeg' || 
      file.type === 'image/png' || 
      file.type === 'image/webp'
    );
    
    if (validFiles.length !== files.length) {
      setError('Only JPEG, PNG and WebP images are supported.');
    }
    
    if (validFiles.length > 0) {
      const newOriginalImages = [...originalImages];
      
      validFiles.forEach(file => {
        // Create URL for preview
        const imageUrl = URL.createObjectURL(file);
        newOriginalImages.push({
          file,
          url: imageUrl,
          name: file.name,
          size: file.size
        });
      });
      
      setOriginalImages(newOriginalImages);
    }
  };

  const removeImage = (index) => {
    const newOriginalImages = [...originalImages];
    URL.revokeObjectURL(newOriginalImages[index].url); // Clean up memory
    newOriginalImages.splice(index, 1);
    setOriginalImages(newOriginalImages);
    
    // Also remove from compressed images if exists
    if (compressedImages[index]) {
      const newCompressedImages = [...compressedImages];
      URL.revokeObjectURL(newCompressedImages[index].url);
      newCompressedImages.splice(index, 1);
      setCompressedImages(newCompressedImages);
    }
  };

  const compressImages = async () => {
    if (originalImages.length === 0) return;
    
    setIsCompressing(true);
    setProgress(0);
    setCompressedImages([]);
    
    const options = {
      maxSizeMB: 1,
      maxWidthOrHeight: 1920,
      useWebWorker: true,
      quality: quality / 100,
    };
    
    const newCompressedImages = [];
    const totalImages = originalImages.length;
    
    for (let i = 0; i < totalImages; i++) {
      try {
        const compressedFile = await imageCompression(originalImages[i].file, options);
        const compressedUrl = URL.createObjectURL(compressedFile);
        
        newCompressedImages.push({
          file: compressedFile,
          url: compressedUrl,
          name: originalImages[i].name,
          originalSize: originalImages[i].size,
          compressedSize: compressedFile.size,
          savings: Math.round(((originalImages[i].size - compressedFile.size) / originalImages[i].size) * 100)
        });
        
        // Update progress
        setProgress(Math.round(((i + 1) / totalImages) * 100));
      } catch (error) {
        console.error('Error compressing image:', error);
      }
    }
    
    setCompressedImages(newCompressedImages);
    setIsCompressing(false);
  };

  const downloadImage = (index) => {
    if (!compressedImages[index]) return;
    
    const link = document.createElement('a');
    link.href = compressedImages[index].url;
    
    // Add '-compressed' to filename before extension
    const fileName = compressedImages[index].name;
    const lastDotIndex = fileName.lastIndexOf('.');
    const newFileName = lastDotIndex !== -1 
      ? fileName.substring(0, lastDotIndex) + '-compressed' + fileName.substring(lastDotIndex)
      : fileName + '-compressed';
    
    link.download = newFileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  
  const downloadAllImages = () => {
    compressedImages.forEach((_, index) => {
      downloadImage(index);
    });
  };
  
  const formatBytes = (bytes, decimals = 2) => {
    if (bytes === 0) return '0 Bytes';
    
    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
  };

  return (
    <Container className="page-container image-reducer-container">
      <div className="tool-header">
        <h1 className="section-title">Image Compressor Pro</h1>
        <p className="text-center tool-description">
          Compress PNG, JPEG, and WebP images instantly with this 100% free & easy-to-use online tool!
        </p>
      </div>
      
      {error && (
        <Alert variant="danger" className="mb-4">
          {error}
        </Alert>
      )}

      <Card className="upload-area-card">
        <Card.Body>
          <div 
            className={`upload-area ${dragActive ? 'drag-active' : ''}`}
            onDragEnter={handleDrag}
            onDragOver={handleDrag}
            onDragLeave={handleDrag}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current.click()}
          >
            <input 
              ref={fileInputRef}
              type="file"
              className="upload-input"
              accept=".jpg,.jpeg,.png,.webp"
              multiple
              onChange={handleChange}
            />
            <div className="upload-icon">
              <i className="la la-cloud-upload-alt" aria-hidden="true"></i>
            </div>
            <h3 className="upload-title">Drag & Drop Images Here</h3>
            <p className="upload-text">
              or <span className="upload-browse">browse files</span> (max 10 images)
            </p>
            <p className="upload-formats">Supported formats: JPEG, PNG, WebP</p>
          </div>
        </Card.Body>
      </Card>

      {originalImages.length > 0 && (
        <div className="mt-5">
          <Card className="settings-card">
            <Card.Body>
              <h3 className="settings-title">Compression Settings</h3>
              <Row className="align-items-center">
                <Col md={4}>
                  <Form.Label htmlFor="qualityRange" className="quality-label">
                    Quality: {quality}%
                  </Form.Label>
                </Col>
                <Col md={6}>
                  <Form.Range
                    id="qualityRange"
                    value={quality}
                    onChange={(e) => setQuality(parseInt(e.target.value))}
                    min={10}
                    max={100}
                  />
                </Col>
                <Col md={2}>
                  <Button 
                    variant="primary" 
                    onClick={compressImages} 
                    disabled={isCompressing}
                    className="compress-btn"
                  >
                    {isCompressing ? (
                      <span>
                        <i className="la la-spinner la-spin" aria-hidden="true"></i> Compressing...
                      </span>
                    ) : (
                      <span>
                        <i className="la la-compress-arrows-alt" aria-hidden="true"></i> Compress
                      </span>
                    )}
                  </Button>
                </Col>
              </Row>
              
              {isCompressing && (
                <ProgressBar 
                  now={progress} 
                  label={`${progress}%`} 
                  className="mt-3 compression-progress"
                />
              )}
            </Card.Body>
          </Card>

          <h3 className="mt-5 mb-3 images-title">
            Your Images <span className="images-count">({originalImages.length}/10)</span>
          </h3>
          
          <Row>
            {originalImages.map((image, index) => (
              <Col lg={4} md={6} key={index} className="mb-4">
                <Card className="image-card">
                  <div className="image-preview-container">
                    <img 
                      src={image.url} 
                      alt={image.name} 
                      className="image-preview" 
                    />
                    <div className="image-actions">
                      <Button 
                        variant="danger" 
                        size="sm" 
                        className="remove-btn"
                        onClick={() => removeImage(index)}
                      >
                        <i className="la la-trash" aria-hidden="true"></i>
                      </Button>
                    </div>
                  </div>
                  <Card.Body>
                    <Card.Title className="image-name">
                      {image.name.length > 18 ? image.name.substring(0, 15) + '...' : image.name}
                    </Card.Title>
                    <Card.Text className="image-info">
                      Original size: {formatBytes(image.size)}
                    </Card.Text>
                    
                    {compressedImages[index] && (
                      <>
                        <Card.Text className="image-info compressed-info">
                          <span>New size: {formatBytes(compressedImages[index].compressedSize)}</span>
                          <span className="savings-badge">
                            <i className="la la-check-circle" aria-hidden="true"></i> {compressedImages[index].savings}% smaller
                          </span>
                        </Card.Text>
                        <Button 
                          variant="success" 
                          size="sm" 
                          className="download-btn"
                          onClick={() => downloadImage(index)}
                        >
                          <i className="la la-download" aria-hidden="true"></i> Download
                        </Button>
                      </>
                    )}
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>

          {compressedImages.length > 1 && (
            <div className="text-center mt-3 mb-5">
              <Button 
                variant="primary" 
                className="download-all-btn"
                onClick={downloadAllImages}
              >
                <i className="la la-download" aria-hidden="true"></i> Download All
              </Button>
            </div>
          )}
        </div>
      )}
    </Container>
  );
};

export default ImageReducer;